
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Sparkles, Wand2, FileText, Hash } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface AIContentGeneratorProps {
  onContentGenerated: (content: string, type: 'title' | 'excerpt' | 'content' | 'tags') => void;
  currentContent?: {
    title?: string;
    excerpt?: string;
    content?: string;
    category?: string;
  };
}

export function AIContentGenerator({ onContentGenerated, currentContent }: AIContentGeneratorProps) {
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingType, setGeneratingType] = useState<string | null>(null);

  const generateContent = async (type: 'title' | 'excerpt' | 'content' | 'tags', customPrompt?: string) => {
    if (!customPrompt && !prompt.trim()) {
      toast({
        title: "Prompt required",
        description: "Please enter a prompt for content generation",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    setGeneratingType(type);

    try {
      const context = {
        title: currentContent?.title,
        excerpt: currentContent?.excerpt,
        category: currentContent?.category,
      };

      const { data, error } = await supabase.functions.invoke('generate-blog-content', {
        body: {
          prompt: customPrompt || prompt,
          type,
          context: Object.entries(context)
            .filter(([_, value]) => value)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ')
        }
      });

      if (error) throw error;

      if (data?.content) {
        onContentGenerated(data.content, type);
        toast({
          title: "Content generated!",
          description: `${type.charAt(0).toUpperCase() + type.slice(1)} has been generated successfully.`,
        });
        
        // Clear prompt after successful generation
        if (!customPrompt) setPrompt("");
      }
    } catch (error) {
      console.error('Error generating content:', error);
      toast({
        title: "Generation failed",
        description: "Failed to generate content. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
      setGeneratingType(null);
    }
  };

  const quickPrompts = [
    {
      type: 'title' as const,
      icon: FileText,
      label: "Generate Title",
      prompt: `Create a compelling blog post title about ${currentContent?.category?.toLowerCase() || 'productivity'} for freelancers`
    },
    {
      type: 'excerpt' as const,
      icon: Wand2,
      label: "Generate Excerpt",
      prompt: `Write an engaging excerpt for a blog post titled "${currentContent?.title || 'this blog post'}"`
    },
    {
      type: 'tags' as const,
      icon: Hash,
      label: "Generate Tags",
      prompt: `Suggest relevant tags for a ${currentContent?.category?.toLowerCase() || 'productivity'} blog post titled "${currentContent?.title || 'this blog post'}"`
    }
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-500" />
          AI Content Generator
          <Badge variant="secondary">Claude 3.5</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Textarea
            placeholder="Describe what you want to write about... (e.g., 'Write about time tracking best practices for remote workers')"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={3}
            className="resize-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={() => generateContent('title')}
            disabled={isGenerating}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            {isGenerating && generatingType === 'title' ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <FileText className="h-3 w-3" />
            )}
            Title
          </Button>

          <Button
            onClick={() => generateContent('excerpt')}
            disabled={isGenerating}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            {isGenerating && generatingType === 'excerpt' ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Wand2 className="h-3 w-3" />
            )}
            Excerpt
          </Button>

          <Button
            onClick={() => generateContent('content')}
            disabled={isGenerating}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            {isGenerating && generatingType === 'content' ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Sparkles className="h-3 w-3" />
            )}
            Content
          </Button>

          <Button
            onClick={() => generateContent('tags')}
            disabled={isGenerating}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            {isGenerating && generatingType === 'tags' ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Hash className="h-3 w-3" />
            )}
            Tags
          </Button>
        </div>

        <div className="border-t pt-4">
          <p className="text-sm text-muted-foreground mb-2">Quick Actions:</p>
          <div className="grid gap-2">
            {quickPrompts.map((item) => {
              const Icon = item.icon;
              return (
                <Button
                  key={item.type}
                  onClick={() => generateContent(item.type, item.prompt)}
                  disabled={isGenerating}
                  variant="ghost"
                  size="sm"
                  className="justify-start gap-2 h-auto p-2 text-left"
                >
                  {isGenerating && generatingType === item.type ? (
                    <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
                  ) : (
                    <Icon className="h-3 w-3 flex-shrink-0" />
                  )}
                  <span className="text-xs">{item.label}</span>
                </Button>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
